import React from "react";
import "./RecordCard.scss";
import { RowAction, RowActionsMenu } from "./RowActions";
import { ToolbarAction } from "./TableToolbar";
import type { ColumnDef, TableModel } from "./types";

/**
 * ONE row, drawn as a labelled card — the record renderer behind
 * ViewSpec.layout "record" (FEATURE-20260831-more-dsls-form-and-record-page).
 *
 * It reads the SAME model the grid reads, so the query, the CDC refresh, the
 * field labels and the cell renderers are shared: this is a second way to draw
 * a view, never a second way to fetch one. A grid with one row is not a record
 * page — every value truncated to a column width, under a search box and a
 * column chooser — which is why glen turned that down on /pagex/monitor/<uid>
 * (2026-08-30).
 */
export interface RecordCardProps {
  model: TableModel<Record<string, unknown>>;
  /** bumped by the view when the model's rows change */
  externalUpdateTrigger?: number;
  /** the record's own actions, shown beside the title */
  actions?: RowAction<Record<string, unknown>>[];
  toolbarActions?: ToolbarAction[];
  /** sections, in order; fields in none of them follow in one unnamed section */
  groups?: Array<{ title: string; fields: string[] }>;
  emptyMessage?: string;
  status?: "loading" | "error" | "gated" | "ready";
  error?: React.ReactNode;
  onRefresh?: () => void;
  refreshing?: boolean;
  /** a status line in the header: the record runs without some columns */
  statusBanner?: React.ReactNode;
}

/** The value cell: the column's own renderer when it has one, else the raw text. */
function renderValue(
  column: ColumnDef<Record<string, unknown>, unknown>,
  row: Record<string, unknown>,
): React.ReactNode {
  if (column.cellRenderer) return column.cellRenderer(row[column.id], row, 0);
  const v = row[column.id];
  if (v === null || v === undefined || v === "") return <span className="record-empty">—</span>;
  return String(v);
}

export function RecordCard({
  model,
  externalUpdateTrigger,
  actions,
  toolbarActions,
  groups,
  emptyMessage,
  status,
  error,
  onRefresh,
  refreshing,
  statusBanner,
}: RecordCardProps) {
  const columns = React.useMemo(
    () => model.getVisibleColumns(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [model.columns, externalUpdateTrigger],
  );
  const row = React.useMemo(
    () => model.data[0],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [model.data, externalUpdateTrigger],
  );

  if (status === "error") return <div className="record-card record-empty-state">{error ?? "Query failed."}</div>;
  if (status === "loading" && !row) return <div className="record-card record-empty-state">Loading…</div>;
  if (!row) return <div className="record-card record-empty-state">{emptyMessage ?? "No such record."}</div>;

  type Col = ColumnDef<Record<string, unknown>, unknown>;
  const byId = new Map<string, Col>(columns.map((c: Col) => [c.id, c]));
  const grouped = groups ?? [];
  const claimed = new Set(grouped.flatMap((g) => g.fields));
  const sections: Array<{ title?: string; columns: Col[] }> = [
    ...grouped.map((g) => ({
      title: g.title,
      columns: g.fields.map((f) => byId.get(f)).filter((c): c is Col => Boolean(c)),
    })),
    { columns: columns.filter((c: Col) => !claimed.has(c.id)) },
  ].filter((s) => s.columns.length > 0);

  return (
    <div className="record-card">
      <div className="record-card-header">
        {statusBanner ? (
          <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", fontSize: "0.85rem" }}>
            {statusBanner}
          </div>
        ) : null}
        <div className="record-card-actions">
          {toolbarActions?.map((a, i) => (
            <button key={i} className="record-card-button" onClick={a.onClick} disabled={a.disabled}>
              {a.label}
            </button>
          ))}
          {onRefresh && (
            <button
              className={`record-card-refresh ${refreshing ? "spinning" : ""}`}
              onClick={onRefresh}
              aria-label="Refresh"
              title="Refresh"
            >
              ⟳
            </button>
          )}
          {actions?.length ? <RowActionsMenu actions={actions} row={row} rowIndex={0} /> : null}
        </div>
      </div>

      {sections.map((section, i) => (
        <div className="record-section" key={i}>
          {section.title && <div className="record-section-title">{section.title}</div>}
          <dl className="record-fields">
            {section.columns.map((c) => (
              <div className="record-field" key={c.id}>
                <dt className="record-label">{c.header}</dt>
                <dd className="record-value">{renderValue(c, row)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}
